import { useState, useEffect, useRef } from 'react';
import { Heart, Stars, Sparkles, Send, PartyPopper } from 'lucide-react';

interface FloatingHeart {
    id: number;
    x: number;
    y: number;
    size: number;
    duration: number;
    delay: number;
}

type Stage = 'welcome' | 'question' | 'accepted';

export function ValentinesDayBody() {
    const [stage, setStage] = useState<Stage>('welcome');
    const [noButtonPos, setNoButtonPos] = useState<{ x: number; y: number }>({ x: 0, y: 0 });
    const [hearts, setHearts] = useState<FloatingHeart[]>([]);
    const containerRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        const initialHearts: FloatingHeart[] = Array.from({ length: 20 }).map((_, i) => ({
            id: i,
            x: Math.random() * 100,
            y: Math.random() * 100,
            size: Math.random() * 20 + 10,
            duration: Math.random() * 10 + 10,
            delay: Math.random() * 5
        }));
        setHearts(initialHearts);
    }, []);

    const moveNoButton = (): void => {
        const x = Math.random() * 300 - 150;
        const y = Math.random() * 300 - 150;
        setNoButtonPos({ x, y });
    };

    const handleAccept = (): void => {
        setStage('accepted');
    };

    return (
        <div
            ref={containerRef}
            className="vday-wrapper"
            style={{
                minHeight: '100vh',
                position: 'relative',
                overflow: 'hidden',
                backgroundColor: '#fff1f2',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                padding: '20px'
            }}
        >
            <div className="position-absolute w-100 h-100 top-0 start-0 pointer-events-none" style={{ zIndex: 0 }}>
                {hearts.map((heart) => (
                    <div
                        key={heart.id}
                        className="position-absolute text-danger opacity-25"
                        style={{
                            left: `${heart.x}%`,
                            top: `${heart.y}%`,
                            animation: `vdayBounce ${heart.duration}s infinite ease-in-out`,
                            animationDelay: `${heart.delay}s`,
                        }}
                    >
                        <Heart size={heart.size} fill="currentColor" />
                    </div>
                ))}
            </div>

            <div className="container position-relative" style={{ zIndex: 10 }}>
                <div className="row justify-content-center">
                    <div className="col-12 col-md-8 col-lg-6">
                        <div className="card-glow-wrapper">
                            <div className="card-glow-effect"></div>
                            <div className="card vday-glass-card border-0 shadow-lg">
                                <div className="card-body p-4 p-sm-5 text-center">

                                    {stage === 'welcome' && (
                                        <div className="animate-fade-in">
                                            <div className="icon-circle-bg mb-4">
                                                <Heart className="text-danger animate-vday-pulse" size={48} fill="currentColor" />
                                            </div>
                                            <h1 className="display-6 fw-bold text-dark mb-3">
                                                Hey Katkouti! <Sparkles className="text-warning d-inline-block" size={28} />
                                            </h1>
                                            <p className="lead text-secondary mb-5">
                                                Someone thinks you're pretty incredible and wanted to send you a little surprise.
                                            </p>
                                            <img src='https://c.tenor.com/F_T9V0Pg0ukAAAAC/tenor.gif' className='mb-5' style={{ height: 300 }} alt='shy guy laughing' />
                                            <button
                                                className="btn btn-danger btn-lg rounded-pill px-5 py-3 fw-bold shadow-sm d-inline-flex align-items-center gap-2"
                                                onClick={() => setStage('question')}
                                            >
                                                Open Your Surprise <Send size={20} />
                                            </button>
                                        </div>
                                    )}

                                    {stage === 'question' && (
                                        <div className="animate-zoom-in">
                                            <div className="d-flex justify-content-center gap-3 mb-4">
                                                <Stars className="text-warning" />
                                                <Heart size={40} className="text-danger" fill="currentColor" />
                                                <Stars className="text-warning" />
                                            </div>
                                            <h2 className="h1 fw-bold text-dark mb-5">
                                                Will you be my Valentine? 🌹
                                            </h2>
                                            <img src='https://media.tenor.com/gKBAgxzAE4kAAAAi/pepe-pepe-shy.gif' alt='pope is shy and waiting' />
                                            <div className="d-flex flex-column flex-sm-row justify-content-center align-items-center gap-4 position-relative" style={{ minHeight: '180px' }}>
                                                <button
                                                    className="btn btn-success btn-lg rounded-pill px-5 py-3 fw-bold shadow transition-scale"
                                                    onClick={handleAccept}
                                                >
                                                    Yes! <PartyPopper size={20} className="ms-2" />
                                                </button>

                                                <button
                                                    className="btn btn-light btn-lg rounded-pill px-4 py-3 fw-bold border text-muted shadow-sm"
                                                    onMouseEnter={moveNoButton}
                                                    onClick={moveNoButton}
                                                    style={{
                                                        transform: `translate(${noButtonPos.x}px, ${noButtonPos.y}px)`,
                                                        transition: 'all 0.3s cubic-bezier(0.34, 1.56, 0.64, 1)',
                                                        zIndex: 5
                                                    }}
                                                >
                                                    No thanks
                                                </button>
                                            </div>
                                            <p className="mt-4 text-muted small fst-italic">P.S. The "No" button is just for decoration ;)</p>
                                        </div>
                                    )}

                                    {stage === 'accepted' && (
                                        <div className="animate-bounce-in">
                                            <div className="mb-4 d-flex justify-content-center gap-2">
                                                {[...Array(3)].map((_, i) => (
                                                    <Heart
                                                        key={i}
                                                        size={32}
                                                        className="text-danger"
                                                        style={{
                                                            animation: 'vdayBounce 1s infinite',
                                                            animationDelay: `${i * 0.2}s`
                                                        }}
                                                        fill="currentColor"
                                                    />
                                                ))}
                                            </div>
                                            <h1 className="display-5 fw-bold text-danger mb-4">Yay! Best Day Ever!</h1>
                                            <img src='https://c.tenor.com/gDfUt8Kry48AAAAC/tenor.gif' className='mb-1' alt='yaaaay!' />
                                            <div className="success-message-box p-4 rounded-4 border border-danger border-dashed">
                                                <p className="h4 text-danger fst-italic mb-3">
                                                    "You always give me butterflies everytime you smile ya zin. I'm happy that you will be my eternal valentine, not just today."
                                                </p>
                                                <div className="d-flex justify-content-center align-items-center gap-2 text-danger opacity-75">
                                                    <span className="fw-bold text-uppercase small" style={{ letterSpacing: '2px' }}>❤️‍🔥Love you katkouti❤️‍🔥</span>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </div>

            <style dangerouslySetInnerHTML={{
                __html: `
        .vday-glass-card {
          background: rgba(255, 255, 255, 0.85) !important;
          backdrop-filter: blur(15px);
          border: 1px solid rgba(255, 255, 255, 0.5) !important;
          border-radius: 2rem !important;
        }
        .card-glow-wrapper {
          position: relative;
        }
        .card-glow-effect {
          position: absolute;
          inset: -10px;
          background: linear-gradient(45deg, #f472b6, #fb7185, #f87171);
          border-radius: 2.5rem;
          filter: blur(20px);
          opacity: 0.2;
          z-index: -1;
        }
        .icon-circle-bg {
          width: 80px;
          height: 80px;
          background-color: #fff1f2;
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          margin: 0 auto;
        }
        .success-message-box {
          background-color: #fff1f2;
          border-style: dashed !important;
          border-width: 2px !important;
        }
        .transition-scale:hover {
          transform: scale(1.1);
        }
        @keyframes vdayBounce {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-20px); }
        }
        @keyframes vdayPulse {
          0% { transform: scale(1); }
          50% { transform: scale(1.15); }
          100% { transform: scale(1); }
        }
        .animate-vday-pulse {
          animation: vdayPulse 1.5s infinite ease-in-out;
        }
        .animate-fade-in { animation: vdayFadeIn 0.8s ease-out; }
        .animate-zoom-in { animation: vdayZoomIn 0.5s ease-out; }
        .animate-bounce-in { animation: vdayBounceIn 0.7s forwards; }

        @keyframes vdayFadeIn {
          from { opacity: 0; transform: translateY(20px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes vdayZoomIn {
          from { opacity: 0; transform: scale(0.9); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes vdayBounceIn {
          0% { transform: scale(0.3); opacity: 0; }
          50% { transform: scale(1.05); opacity: 1; }
          70% { transform: scale(0.9); }
          100% { transform: scale(1); }
        }
      `}} />
        </div>
    );
}